import { useEffect, useState } from "react";
import api, { formatPrice, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = ["pending", "processing", "shipped", "delivered", "cancelled", "returned"];
const PAYMENT_STATUS = ["pending", "paid", "refunded"];
const CARRIERS = ["Correos", "Correos Express", "SEUR", "MRW", "GLS", "DHL", "UPS", "FedEx"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = (status = "") => {
    const url = status ? `/admin/orders?status=${status}` : "/admin/orders";
    api.get(url).then(r => setOrders(r.data));
  };

  useEffect(() => { load(filter); }, [filter]);

  const updateStatus = async (id, changes) => {
    try {
      await api.put(`/admin/orders/${id}/status`, changes);
      toast.success("Pedido actualizado");
      load(filter);
      if (selected) setSelected({ ...selected, ...changes });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-orders">
      <p className="overline mb-3">Logística</p>
      <h1 className="font-heading text-4xl text-ivory mb-8">Pedidos</h1>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="status-filters">
        <button onClick={() => setFilter("")} className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border ${!filter ? "border-gold text-gold" : "border-smoke text-ivory/60"}`}>Todos</button>
        {STATUS_OPTIONS.map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border ${filter === s ? "border-gold text-gold" : "border-smoke text-ivory/60"}`}>{s}</button>
        ))}
      </div>

      <div className="bg-graphite border border-smoke overflow-x-auto">
        <table className="admin-table w-full">
          <thead>
            <tr><th>Pedido</th><th>Cliente</th><th>Fecha</th><th>Total</th><th>Estado</th><th>Pago</th><th></th></tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} data-testid={`order-${o.id}`}>
                <td className="text-ivory">#{o.id.slice(0, 8).toUpperCase()}</td>
                <td className="text-ivory/70">{o.shipping_address?.full_name || o.user_email || "Invitado"}</td>
                <td className="text-ivory/70">{new Date(o.created_at).toLocaleDateString("es-ES")}</td>
                <td className="text-gold">{formatPrice(o.total)}</td>
                <td><span className="text-[10px] uppercase tracking-[0.15em] text-ivory/80">{o.status}</span></td>
                <td><span className={`text-[10px] uppercase tracking-[0.15em] ${o.payment_status === "paid" ? "text-gold" : "text-ivory/60"}`}>{o.payment_status}</span></td>
                <td><button onClick={() => setSelected(o)} className="text-gold text-xs uppercase tracking-wider" data-testid={`view-${o.id}`}>Detalle</button></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="7" className="text-center text-ivory/40 py-12">Sin pedidos</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[100] bg-carbon/90 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-graphite border border-smoke max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-smoke flex justify-between">
              <div>
                <p className="overline mb-1">Pedido</p>
                <h2 className="font-heading text-3xl text-ivory">#{selected.id.slice(0, 8).toUpperCase()}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-ivory">✕</button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="overline mb-2">Cliente</p>
                  <p className="text-ivory">{selected.shipping_address?.full_name}</p>
                  <p className="text-ivory/60 text-sm">{selected.user_email}</p>
                  <p className="text-ivory/60 text-sm">{selected.shipping_address?.phone}</p>
                </div>
                <div>
                  <p className="overline mb-2">Dirección</p>
                  <p className="text-ivory/80 text-sm">{selected.shipping_address?.address_line1}</p>
                  <p className="text-ivory/80 text-sm">{selected.shipping_address?.city}, {selected.shipping_address?.postal_code}</p>
                  <p className="text-ivory/80 text-sm">{selected.shipping_address?.country}</p>
                </div>
              </div>

              <div>
                <p className="overline mb-2">Productos</p>
                {selected.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-smoke/50">
                    <div className="flex gap-3">
                      {it.image && <img src={it.image} alt="" className="w-10 h-12 object-cover" />}
                      <div><p className="text-ivory text-sm">{it.name}</p><p className="text-ivory/40 text-xs">x{it.quantity}</p></div>
                    </div>
                    <p className="text-gold text-sm">{formatPrice(it.price * it.quantity)}</p>
                  </div>
                ))}
                <div className="flex justify-between mt-4 pt-4 border-t border-smoke">
                  <span className="font-heading text-xl text-ivory">Total</span>
                  <span className="font-heading text-2xl text-gold">{formatPrice(selected.total)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-smoke">
                <div>
                  <label className="luxe-label">Estado</label>
                  <select value={selected.status} onChange={e => updateStatus(selected.id, { status: e.target.value })} className="luxe-input border-b border-smoke">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s} className="bg-carbon">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="luxe-label">Estado pago</label>
                  <select value={selected.payment_status} onChange={e => updateStatus(selected.id, { payment_status: e.target.value })} className="luxe-input border-b border-smoke">
                    {PAYMENT_STATUS.map(s => <option key={s} value={s} className="bg-carbon">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="luxe-label">Transportista</label>
                  <select value={selected.carrier || ""} onChange={e => updateStatus(selected.id, { carrier: e.target.value })} className="luxe-input border-b border-smoke">
                    <option value="" className="bg-carbon">-</option>
                    {CARRIERS.map(c => <option key={c} value={c} className="bg-carbon">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="luxe-label">Tracking</label>
                  <input data-testid="tracking-input" placeholder="Nº seguimiento" defaultValue={selected.tracking_number || ""} onBlur={e => updateStatus(selected.id, { tracking_number: e.target.value })} className="luxe-input" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
